import { useEffect, useContext, useState } from 'react';
import { FlightDetails } from '@tff/types/tff';
import { CancelFlightByRecordLocator, CancelFlightResponse } from '@tff/types/tff/cancel-flight-service';

import { LoadingContext } from '../contexts/LoadingContextProvider';
import { apigeeEndpoint } from '../apis';
import { shouldSkipRefundCheck } from '../util/cancel-booking-utils';

export type TypeOfStatus = 'IN_PROGRESS' | 'CHECKED' | 'FAILURE';

export type CheckRefundProps = {
  checkRefundRequest?: CancelFlightByRecordLocator;
  flightDetails?: FlightDetails;
};

export type CheckRefundResponse = {
  checkRefundResponse?: CancelFlightResponse;
  status?: TypeOfStatus;
  error?: any;
  hasPayment?: boolean;
  isVoidable?: boolean;
};

//
export const useCheckCancelRefund = (checkRefundProps: CheckRefundProps): CheckRefundResponse => {
  const [status, setStatus] = useState<TypeOfStatus>();
  const [checkRefundResponse, setCheckRefundResponse] = useState<CancelFlightResponse>();
  const [error, setError] = useState<unknown | Error | undefined>();
  const { showLoading, closeLoading, isLoading } = useContext(LoadingContext);

  useEffect(() => {
    if (!isLoading && status === 'IN_PROGRESS') {
      showLoading('Checking Refund...');
    }
  }, [status, isLoading]);

  useEffect(() => {
    const checkRefundForCancellationAtBackend = async (checkRefundRequest: CancelFlightByRecordLocator): Promise<void> => {
      try {
        setStatus('IN_PROGRESS');
        const response = await apigeeEndpoint.post(`/cancel/${checkRefundRequest.bookingSource}`, {
          ...checkRefundRequest,
          action: 'check-refund-status',
        });
        if (response.data.status === 'FAILURE') {
          setError(response.data.error);
          setStatus('FAILURE');
        }
        setStatus('CHECKED');
        setCheckRefundResponse(response.data.data ?? response.data);
      } catch (error) {
        setError(error);
        setStatus('FAILURE');
      } finally {
        closeLoading();
      }
    };

    if (checkRefundProps?.checkRefundRequest && !status) {
      if (checkRefundProps.flightDetails) {
        if (shouldSkipRefundCheck(checkRefundProps.flightDetails, checkRefundProps.checkRefundRequest.bookingSource)) {
          setStatus('CHECKED');
          return;
        }
      }

      void checkRefundForCancellationAtBackend(checkRefundProps.checkRefundRequest);
    }
  }, [checkRefundProps]);

  return { error, checkRefundResponse, status };
};
